import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axiosInstance from '../../../../../shared/services/api/axiosInstance';
import { formatNumber, formatCurrency } from '../../../../../shared/utils/numberFormatter';

const SourceChart = ({ teamId, appliedFilters, isLoading: parentLoading }) => {
  const [touchPoint, setTouchPoint] = useState('last');
  const [sourceData, setSourceData] = useState([]);
  const [uniqueSources, setUniqueSources] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  // Format source name to show clean domain
  const formatSourceName = (source) => {
    if (!source || source === 'Direct') {
      return 'Direct';
    }

    try {
      let cleaned = source;
      
      // Handle android-app format first: android-app://com.linkedin.android -> linkedin.android
      if (source.includes('android-app://')) {
        cleaned = source.replace(/^android-app:\/\/?\/?/i, '');
        const parts = cleaned.split('/')[0].split('.');
        if (parts.length >= 3) {
          // Take last two parts: com.linkedin.android -> linkedin.android
          cleaned = parts.slice(-2).join('.');
        } else {
          cleaned = cleaned.split('/')[0];
        }
      } else {
        // Remove protocol (http://, https://, etc.)
        cleaned = cleaned.replace(/^[a-z]+:\/\/?\/?/i, '');
        
        // Remove path and query parameters (everything after first /)
        cleaned = cleaned.split('/')[0];
        
        // Remove port if present (e.g., example.com:8080)
        cleaned = cleaned.split(':')[0];
        
        // Remove www. prefix if present
        cleaned = cleaned.replace(/^www\./i, '');
        
        // Remove subdomain prefix for domains with more than 2 parts (e.g., l.instagram.com -> instagram.com)
        const parts = cleaned.split('.');
        if (parts.length > 2) {
          // Remove single character or very short subdomains (l, m, etc.)
          const firstPart = parts[0];
          if (firstPart.length <= 1 || (firstPart.length === 2 && ['m', 'api', 'app'].includes(firstPart.toLowerCase()))) {
            cleaned = parts.slice(1).join('.');
          }
        }
      }
      
      // Capitalize first letter only (Instagram.com, not Instagram.Com)
      cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
      
      return cleaned;
    } catch (error) {
      // If parsing fails, return original
      return source;
    }
  };

  // Touch Point Toggle Component
  const TouchPointToggle = ({ value, onChange }) => (
    <div className="flex border overflow-hidden" style={{ borderColor: styles.borderColor }}>
      <button
        onClick={() => onChange('first')}
        className="px-3 py-1 text-xs transition-colors"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          backgroundColor: value === 'first' ? styles.accentColor : styles.primaryBg,
          color: value === 'first' ? '#ffffff' : styles.textPrimary
        }}
      >
        First Touch Point
      </button>
      <button
        onClick={() => onChange('last')}
        className="px-3 py-1 text-xs transition-colors"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          backgroundColor: value === 'last' ? styles.accentColor : styles.primaryBg,
          color: value === 'last' ? '#ffffff' : styles.textPrimary
        }}
      >
        Last Touch Point
      </button>
    </div>
  );

  // Fetch source data
  useEffect(() => {
    const fetchSourceData = async () => {
      if (!teamId) {
        setSourceData([]);
        return;
      }

      setIsLoading(true);
      try {
        // Build query parameters
        const params = {
          team_id: teamId,
          touchPoint: touchPoint
        };

        // Add website filters if applied (comma-separated string format for this endpoint)
        if (appliedFilters?.websites && appliedFilters.websites.length > 0) {
          params.websites = appliedFilters.websites.join(',');
        }

        // Add date range if specified
        if (appliedFilters?.dateRange?.from && appliedFilters?.dateRange?.to) {
          params.from = appliedFilters.dateRange.from;
          params.to = appliedFilters.dateRange.to;
        }

        const response = await axiosInstance.get('/overview/source-metrics', { params });

        if (response.data?.success && response.data?.sources) {
          // Transform data for bar chart
          const transformedData = response.data.sources.map((item, index) => {
            const formattedSource = formatSourceName(item.source);
            return {
              source: item.source, // Keep original for tooltip
              formattedSource: formattedSource, // Formatted version for display
              userCount: item.userCount,
              web3Users: item.web3Users || 0,
              walletsConnected: item.walletsConnected || 0,
              walletsTransacted: item.walletsTransacted || 0,
              valueTransacted: item.valueTransacted || 0,
              networth: item.networth || 0,
              displaySource: formattedSource
            };
          });

          setSourceData(transformedData);
          setUniqueSources(response.data.uniqueSources || 0);
        } else {
          setSourceData([]);
          setUniqueSources(0);
        }
      } catch (error) {
        console.error('Error fetching source data:', error);
        setSourceData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSourceData();
  }, [teamId, touchPoint, appliedFilters]);

  // Custom Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-lg" style={{ borderRadius: '4px' }}>
          <p className="font-medium text-[#0f172a] mb-2" style={{ fontFamily: 'Raleway, sans-serif' }}>
            {data.source}
          </p>
          <p style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }} className="text-sm mb-1">
            Users: {data.userCount?.toLocaleString() || 0}
          </p>
          <p style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }} className="text-sm mb-1">
            Web3 Users: {data.web3Users?.toLocaleString() || 0}
          </p>
          <p style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }} className="text-sm mb-1">
            Wallets Connected: {data.walletsConnected?.toLocaleString() || 0}
          </p>
          <p style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }} className="text-sm mb-1">
            Wallets Transacted: {data.walletsTransacted?.toLocaleString() || 0}
          </p>
          <p style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }} className="text-sm mb-1">
            Value Transacted: {formatNumber(data.valueTransacted || 0)}
          </p>
          <p style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }} className="text-sm">
            Net Worth: {formatCurrency(data.networth || 0, '$', 1)}
          </p>
        </div>
      );
    }
    return null;
  };

  const loading = parentLoading || isLoading;

  return (
    <div className="p-4 md:p-6" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
          Top Sources
        </h3>
        <TouchPointToggle 
          value={touchPoint} 
          onChange={setTouchPoint}
        />
      </div>
      
      <div className="h-80">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
              Loading source data...
            </div>
          </div>
        ) : sourceData.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
              No source data available
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={sourceData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} horizontal={true} />
              <XAxis 
                dataKey="displaySource"
                tick={{ 
                  fill: styles.textPrimary, 
                  fontSize: 11,
                  fontFamily: 'Raleway, sans-serif'
                }}
                axisLine={{ stroke: styles.borderColor }}
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis 
                tick={{ 
                  fill: styles.textPrimary, 
                  fontSize: 11,
                  fontFamily: 'Raleway, sans-serif'
                }}
                axisLine={{ stroke: styles.borderColor }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="userCount" 
                fill={styles.accentColor}
                name="Users"
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      
      {/* Unique Sources count at bottom right */}
      <div className="flex justify-end mt-2">
        <div className="text-xs" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          Unique Sources: <span style={{ color: styles.textPrimary, fontWeight: '600' }}>{uniqueSources}</span>
        </div>
      </div>
    </div>
  );
};

export default SourceChart;

